import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../firebase';
import { getPlayerKey, getUserId } from '../identity';
import { emitCommand } from '../socket';
import { setBusy, setGuest, setNaming, setProfile } from '../services/AccountSlice';
import { setBody, setType } from '../services/NotificationSlice';

// Signing in is optional. Firebase keeps the Google sign-in; the game server
// links this browser to the account (proving it with the browser's own player
// key) and gives back the account's profile. Every account call sends a fresh
// Firebase ID token, which the server checks.

const NAME_PROMPT_KEY = 'account:namePrompted';

export const idToken = async () => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Please sign in again');
    }
    return user.getIdToken();
};

/** Runs for the life of the app: keeps the store in step with Firebase. */
export const watchAccount = (dispatch) => onAuthStateChanged(auth, async (user) => {
    if (!user) {
        dispatch(setGuest());
        return;
    }
    // Google said yes; now linking this browser to the account.
    dispatch(setBusy(true));
    try {
        const profile = await emitCommand('account:signIn', {
            idToken: await user.getIdToken(),
            userId: getUserId(),
            key: getPlayerKey()
        });
        dispatch(setProfile(profile));
        // The first time on this browser with no name yet: offer to choose one.
        let prompted = false;
        try {
            prompted = localStorage.getItem(NAME_PROMPT_KEY) === '1';
            localStorage.setItem(NAME_PROMPT_KEY, '1');
        } catch (error) { /* private mode */ }
        if (!profile.name && !prompted) {
            dispatch(setNaming(true));
        }
    } catch (error) {
        // The server would not link this browser: stay a guest here.
        await firebaseSignOut(auth).catch(() => {});
        dispatch(setGuest());
        dispatch(setType('error'));
        dispatch(setBody(error.message || 'Could not sign in'));
    }
});

// The Google window is its own feedback, so the button is not put into a
// waiting state while it is open: Google's sign-in page stops this page from
// seeing the window close, so Firebase only notices a closed window several
// seconds later, and a button stuck "busy" all that time looks broken.
// Clicking again simply opens the window again.
export const signIn = async (dispatch) => {
    try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await signInWithPopup(auth, provider);
        // watchAccount takes it from here.
    } catch (error) {
        if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
            return;
        }
        dispatch(setType('error'));
        dispatch(setBody(error?.code === 'auth/popup-blocked'
            ? 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.'
            : 'Could not sign in. Please try again.'));
    }
};

/** Signs this browser out: what it plays from now on counts as a guest's. */
export const signOut = async (dispatch) => {
    try {
        await emitCommand('account:signOut', { idToken: await idToken(), userId: getUserId() });
    } catch (error) { /* signing out locally still matters */ }
    try {
        localStorage.removeItem(NAME_PROMPT_KEY);
    } catch (error) { /* private mode */ }
    await firebaseSignOut(auth);
    dispatch(setGuest());
};

export const setLeaderboardName = async (dispatch, name) => {
    const profile = await emitCommand('account:setName', { idToken: await idToken(), name });
    dispatch(setProfile(profile));
    return profile;
};

export const nameAvailable = async (name) => {
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    return emitCommand('account:nameAvailable', { name, idToken: token });
};

/** Deletes the account here and at Firebase. */
export const deleteAccount = async (dispatch) => {
    await emitCommand('account:delete', { idToken: await idToken() });
    const user = auth.currentUser;
    // Firebase only deletes a user who signed in recently; if not, signing
    // out is enough — nothing about them is left on our side.
    await user?.delete().catch(() => firebaseSignOut(auth));
    try {
        localStorage.removeItem(NAME_PROMPT_KEY);
    } catch (error) { /* private mode */ }
    dispatch(setGuest());
};

/** My games: the account's when signed in, otherwise this browser's. */
export const fetchMyGames = async (before) => {
    if (auth.currentUser) {
        return emitCommand('account:games', { idToken: await idToken(), before: before || null });
    }
    return emitCommand('account:games', { userId: getUserId(), key: getPlayerKey(), before: before || null });
};
